import React, { useState, useRef, useEffect } from 'react';
import { B } from '../constants';
import { borderRadius } from '../designTokens';
import { ai as aiAPI } from '../api';
import CityProse from '../components/CityProse';
import PlaceSheet, { categoryLabel } from '../components/PlaceSheet';
import { PlaceCards, PlacesMap } from '../components/ChatPlaces';
import { trackActivity } from '../lib/activityEvents';

const QUICK_CHIPS = [
  { emoji: '🏖️', text: 'Beach getaway under $1500' },
  { emoji: '🏙️', text: '5 days in Tokyo' },
  { emoji: '🌴', text: 'Best time to visit Bali' },
];

const SUGGESTION_CHIPS = [
  { emoji: '✨', text: 'Build full itinerary', action: 'planner' },
  { emoji: '🔍', text: 'Show destinations', action: 'home' },
];

const KNOWN_DESTINATIONS = [
  'Bali', 'Paris', 'Tokyo', 'New York', 'Santorini', 'Dubai', 'London', 'Rome',
  'Barcelona', 'Bangkok', 'Maldives', 'Swiss Alps', 'Iceland', 'Sydney',
  'Marrakech', 'Lisbon', 'Amsterdam', 'Berlin', 'Prague', 'Vienna', 'Istanbul',
  'Kyoto', 'Osaka', 'Seoul', 'Singapore', 'Hong Kong', 'Hanoi', 'Ho Chi Minh',
  'Phuket', 'Chiang Mai', 'Kuala Lumpur', 'Jakarta', 'Taipei',
  'Mumbai', 'Delhi', 'Goa', 'Jaipur', 'Kerala', 'Cape Town', 'Marrakesh',
  'Cairo', 'Nairobi', 'Zanzibar', 'Athens', 'Mykonos', 'Crete', 'Florence',
  'Venice', 'Milan', 'Naples', 'Madrid', 'Seville', 'Porto', 'Dublin',
  'Edinburgh', 'Reykjavik', 'Oslo', 'Stockholm', 'Copenhagen', 'Helsinki',
  'Budapest', 'Krakow', 'Warsaw', 'Zurich', 'Geneva', 'Munich', 'Hamburg',
  'Buenos Aires', 'Rio de Janeiro', 'Lima', 'Cusco', 'Machu Picchu',
  'Mexico City', 'Cancun', 'Tulum', 'Costa Rica', 'Patagonia', 'Galapagos',
  'Toronto', 'Vancouver', 'Montreal', 'Banff', 'Los Angeles', 'San Francisco',
  'Las Vegas', 'Miami', 'Chicago', 'Seattle', 'Boston', 'Hawaii', 'Maui',
  'Honolulu',
];

function extractDestinationFromReply(reply) {
  if (!reply || typeof reply !== 'string') return null;
  const lower = reply.toLowerCase();
  for (const name of KNOWN_DESTINATIONS) {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase()}\\b`);
    if (re.test(lower)) return name;
  }
  return null;
}

// Verified places the reply recommended, as a swipeable row of cards.
export default function AIChatScreen({ goScreen, user, onAuthRequired }) {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      text: "Hi! I'm your travel assistant 🌍 Where are you dreaming of going, or what kind of trip are you after?",
      isUser: false,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hoveredPlace, setHoveredPlace] = useState(null);
  const [openPlace, setOpenPlace] = useState(null);
  const [mapMessageId, setMapMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    if (!user) { onAuthRequired(); return; }

    const userMsg = { id: Date.now(), text: text.trim(), isUser: true };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);
    trackActivity('chat', 'chat_message_sent', { length: text.trim().length });

    try {
      const context = updatedMessages.slice(-6).map(m => `${m.isUser ? 'User' : 'Assistant'}: ${m.text}`).join('\n');
      const startedAt = Date.now();
      const data = await aiAPI.chat(text.trim(), context);
      trackActivity('chat', 'chat_response_received', { durationMs: Date.now() - startedAt });
      const destination = data.city?.name || extractDestinationFromReply(data.reply);
      const places = Array.isArray(data.places) ? data.places : [];
      const id = Date.now() + 1;
      setMessages(prev => [...prev, { id, text: data.reply, isUser: false, destination, city: data.city || null, places }]);
      if (places.length) setMapMessageId(id);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: "Sorry, I couldn't process that right now. Please try again in a moment.",
        isUser: false,
      }]);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleChip = (text) => {
    sendMessage(text);
  };

  const mapMessage = messages.find(m => m.id === mapMessageId && m.places?.length);

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', paddingBottom: 80 }}>
      <style>{`
        .gb-chat-layout { display: flex; flex-direction: column; gap: 16px; }
        .gb-chat-map-wide { display: none; }
        @media (min-width: 960px) {
          .gb-chat-layout.has-map { display: grid; grid-template-columns: minmax(0, 1fr) 400px; align-items: start; }
          .gb-chat-layout.has-map .gb-chat-map-wide { display: block; position: sticky; top: 90px; }
          .gb-chat-layout.has-map .gb-chat-map-inline { display: none; }
        }
      `}</style>
      <div style={{ maxWidth: mapMessage ? 1200 : 760, margin: '0 auto', padding: '40px clamp(16px, 3vw, 40px)' }}>
        <div style={{ marginBottom: 28, position: 'relative' }}>
          <button onClick={() => goScreen('home')} style={{ position: 'absolute', left: 0, top: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: B.night, padding: 4 }}>←</button>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, color: B.forest, fontFamily: "'Montserrat',sans-serif" }}>Ask anything</span>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 42, fontWeight: 800, marginTop: 6 }}>AI Travel Assistant</h1>
          </div>
        </div>

        <div className={`gb-chat-layout${mapMessage ? ' has-map' : ''}`}>
        <div style={{
          background: '#fff', border: '1px solid #e7e2d9', borderRadius: borderRadius.lg,
          overflow: 'hidden', boxShadow: '0 6px 20px rgba(15,25,35,0.10)', minWidth: 0,
        }}>
          <div style={{
            background: B.night, color: '#fff', padding: '16px 20px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ width: 32, height: 32, borderRadius: '50%', background: B.mint, display: 'grid', placeItems: 'center' }}>
              🤖
            </span>
            <div>
              <b style={{ fontSize: 15 }}>GreatBreak AI</b>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Always here to help plan</div>
            </div>
          </div>

          <div style={{ padding: 22, minHeight: 300, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 500, overflowY: 'auto' }}>
            {messages.map(msg => (
              <React.Fragment key={msg.id}>
                {msg.isUser ? (
                  <div style={{
                    maxWidth: '78%', padding: '12px 16px', borderRadius: 16,
                    background: B.forest, color: '#fff', alignSelf: 'flex-end',
                    borderBottomRightRadius: 4, fontSize: 14, lineHeight: 1.5,
                    wordBreak: 'break-word',
                  }}>
                    {msg.text}
                  </div>
                ) : (
                  <div style={{
                    maxWidth: '78%', padding: '12px 16px', borderRadius: 16,
                    background: B.sand, border: '1px solid #e7e2d9', alignSelf: 'flex-start',
                    borderBottomLeftRadius: 4, fontSize: 14, lineHeight: 1.5,
                    wordBreak: 'break-word',
                  }}>
                    <CityProse text={String(msg.text || '').replace(/^\s*([-*_])\1{2,}\s*$/gm, '')} style={{ fontSize: 14, lineHeight: 1.6, margin: '0 0 8px' }} />
                    {msg.places?.length > 0 && (
                      <>
                        <PlaceCards places={msg.places} onOpen={(p) => setOpenPlace({ place: p, city: msg.city })} onHover={setHoveredPlace} />
                        {msg.id !== mapMessageId && (
                          <button type="button" onClick={() => setMapMessageId(msg.id)}
                            style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, color: B.forest, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                            📍 Show these on the map
                          </button>
                        )}
                      </>
                    )}
                    {msg.text === messages[messages.length - 1]?.text && msg.destination && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                        {SUGGESTION_CHIPS.map((chip, i) => (
                          <button key={i} onClick={() => chip.action === 'planner' ? goScreen('ai', { prefillDestination: msg.destination }) : goScreen('home')}
                            style={{
                              border: '1.5px solid var(--forest, #1a6b4a)', color: B.forest, background: '#fff',
                              borderRadius: borderRadius.pill, padding: '7px 14px', fontSize: 13, fontWeight: 600,
                              cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = B.forest; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = B.forest; }}>
                            {chip.emoji} {chip.text}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}

            {isTyping && (
              <div style={{
                maxWidth: '78%', padding: '12px 16px', borderRadius: 16,
                background: B.sand, border: '1px solid #e7e2d9', alignSelf: 'flex-start',
                borderBottomLeftRadius: 4, fontSize: 18, lineHeight: 1, color: B.muted,
              }}>
                …
              </div>
            )}

            {messages.length <= 1 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {QUICK_CHIPS.map((chip, i) => (
                  <button key={i} onClick={() => handleChip(chip.text)}
                    style={{
                      border: '1.5px solid var(--forest, #1a6b4a)', color: B.forest, background: '#fff',
                      borderRadius: borderRadius.pill, padding: '7px 14px', fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = B.forest; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = B.forest; }}>
                    {chip.emoji} {chip.text}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div style={{ display: 'flex', gap: 10, borderTop: '1px solid #e7e2d9', padding: 14 }}>
            <input ref={inputRef} id="chatInput"
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={user ? "Type your message..." : "Sign in to chat with your travel assistant"}
              disabled={!user || isTyping}
              style={{
                flex: 1, border: '1px solid #e7e2d9', borderRadius: borderRadius.pill,
                padding: '12px 18px', fontFamily: "'Montserrat',sans-serif",
                fontSize: 14, outline: 'none', background: '#fff',
              }} />
            <button onClick={() => sendMessage(input)}
              disabled={!input.trim() || isTyping || !user}
              style={{
                background: input.trim() && !isTyping && user ? B.forest : B.muted,
                color: '#fff', border: 'none', borderRadius: borderRadius.pill,
                padding: '12px 22px', fontWeight: 700, fontSize: 14,
                cursor: input.trim() && !isTyping && user ? 'pointer' : 'not-allowed',
                fontFamily: "'Montserrat',sans-serif",
              }}>
              Send
            </button>
          </div>
        </div>
        {mapMessage && (
          <>
            <div className="gb-chat-map-inline">
              <PlacesMap city={mapMessage.city} places={mapMessage.places} hovered={hoveredPlace} onSelect={(p) => p && setOpenPlace({ place: p, city: mapMessage.city })} height={240} />
            </div>
            <div className="gb-chat-map-wide">
              <PlacesMap city={mapMessage.city} places={mapMessage.places} hovered={hoveredPlace} onSelect={(p) => p && setOpenPlace({ place: p, city: mapMessage.city })} height={520} />
            </div>
          </>
        )}
        </div>
      </div>
      {openPlace && (
        <PlaceSheet place={openPlace.place} cityName={openPlace.city?.name} categoryLabel={categoryLabel}
          center={openPlace.city ? { lat: openPlace.city.lat, lng: openPlace.city.lng } : null}
          onClose={() => setOpenPlace(null)} />
      )}
    </div>
  );
}
